import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { ATTRIBUTE_TYPES } from 'dashboard/components-next/ConversationWorkflow/constants';

/**
 * Composable for managing conversation required attributes workflow
 *
 * This handles the logic for checking if conversations have all required
 * custom attributes filled before they can be resolved.
 */
export function useConversationRequiredAttributes() {
  const { currentAccount, accountId } = useAccount();
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );
  const conversationAttributes = useMapGetter(
    'attributes/getConversationAttributes'
  );

  const isFeatureEnabled = computed(() =>
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONVERSATION_REQUIRED_ATTRIBUTES
    )
  );

  const requiredAttributeKeys = computed(() => {
    if (!isFeatureEnabled.value) return [];
    return (
      currentAccount.value?.settings?.conversation_required_attributes || []
    );
  });

  // [AstraChat] Escopo da regra (caixas/times/agentes). Vazio = global (upstream).
  const requiredAttributesScope = computed(
    () =>
      currentAccount.value?.settings?.conversation_required_attributes_scope ||
      {}
  );

  /**
   * [AstraChat] Diz se a regra se aplica à conversa. Com todas as listas vazias,
   * preserva o comportamento original (global). Com qualquer lista preenchida, a
   * conversa precisa casar com pelo menos uma delas (caixa OU time OU agente).
   * Sem a conversa em mãos, não bloqueia (fail-open, igual ao gate do CAPI).
   */
  const isConversationInScope = conversation => {
    const scope = requiredAttributesScope.value || {};
    const inboxIds = scope.inbox_ids || [];
    const teamIds = scope.team_ids || [];
    const agentIds = scope.agent_ids || [];
    if (!inboxIds.length && !teamIds.length && !agentIds.length) return true;
    if (!conversation) return true;
    const teamId = conversation.meta?.team?.id ?? conversation.team_id;
    const assigneeId =
      conversation.meta?.assignee?.id ?? conversation.assignee_id;
    return (
      inboxIds.includes(conversation.inbox_id) ||
      (teamId != null && teamIds.includes(teamId)) ||
      (assigneeId != null && agentIds.includes(assigneeId))
    );
  };

  const allAttributeOptions = computed(() =>
    (conversationAttributes.value || []).map(attribute => ({
      ...attribute,
      value: attribute.attributeKey,
      label: attribute.attributeDisplayName,
      type: attribute.attributeDisplayType,
      attributeValues: attribute.attributeValues,
    }))
  );

  /**
   * Get the full attribute definitions for only the required attributes
   * Filters allAttributeOptions to only include attributes marked as required
   */
  const requiredAttributes = computed(
    () =>
      requiredAttributeKeys.value
        .map(key =>
          allAttributeOptions.value.find(attribute => attribute.value === key)
        )
        .filter(Boolean) // Remove any undefined attributes (deleted attributes)
  );

  /**
   * Check if a conversation is missing any required attributes
   *
   * @param {Object} conversationCustomAttributes - Current conversation's custom attributes
   * @param {Object|null} conversation - [AstraChat] the conversation, for scope filtering
   * @returns {Object} - Analysis result with missing attributes info
   */
  const checkMissingAttributes = (
    conversationCustomAttributes = {},
    conversation = null
  ) => {
    // If no attributes are required, conversation can be resolved
    if (!requiredAttributes.value.length) {
      return { hasMissing: false, missing: [] };
    }

    // [AstraChat] fora do escopo configurado → regra não se aplica
    if (conversation && !isConversationInScope(conversation)) {
      return { hasMissing: false, missing: [], all: requiredAttributes.value };
    }

    // Find attributes that are missing or empty
    const missing = requiredAttributes.value.filter(attribute => {
      const value = conversationCustomAttributes[attribute.value];

      // A checkbox is filled once true or false is picked. An explicit null is
      // an unanswered prompt, which is how the modal and the backend read it.
      if (attribute.type === ATTRIBUTE_TYPES.CHECKBOX) {
        return value == null;
      }

      // For other attribute types, only consider null, undefined, empty string, or whitespace-only as missing
      // Allow falsy values like 0, false as they are valid filled values
      return value == null || String(value).trim() === '';
    });

    return {
      hasMissing: missing.length > 0,
      missing,
      all: requiredAttributes.value,
    };
  };

  return {
    requiredAttributeKeys,
    requiredAttributes,
    requiredAttributesScope,
    isConversationInScope,
    checkMissingAttributes,
  };
}
